import { NavFooter } from '@app/components/nav-footer';
import { NavMain } from '@app/components/nav-main';
import { NavUser } from '@app/components/nav-user';
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@app/components/ui/sidebar';
import useStructure from '@app/hooks/use-structure';
import AppLogo from './app-logo';
import AppLink from '@app/components/app-link';
import { useAuth } from '@modules/auth/hooks/context/AuthContext';
import { type NavItem } from '@app/types';

export function AppSidebar() {
    const { mainNavItems, footerNavItems } = useStructure();
    const { hasPermission } = useAuth();

    const filterAllowedItems = (
        items: NavItem[],
        inherited: Pick<NavItem, 'permissions' | 'anyPermissions'> = {},
    ): NavItem[] => items.flatMap((item) => {
        const effectivePermissions = item.permissions ?? inherited.permissions;
        const effectiveAnyPermissions = item.anyPermissions ?? inherited.anyPermissions;
        const canAccessItem = (!effectivePermissions || hasPermission(effectivePermissions)) &&
            (!effectiveAnyPermissions?.length || effectiveAnyPermissions.some(hasPermission));
        const children = item.items
            ? filterAllowedItems(item.items, {
                permissions: effectivePermissions,
                anyPermissions: effectiveAnyPermissions,
            })
            : undefined;
        const hasVisibleChildren = Boolean(children?.length);
        const hasExplicitAccessRule = Boolean(item.permissions || item.anyPermissions?.length);

        if (!canAccessItem && !hasVisibleChildren) return [];
        if (item.items && !hasVisibleChildren && !hasExplicitAccessRule) return [];

        return [{
            ...item,
            url: canAccessItem ? item.url : undefined,
            items: children,
        }];
    });

    const visibleMainItems = filterAllowedItems(mainNavItems);
    const visibleFooterItems = filterAllowedItems(footerNavItems);

    // noinspection HtmlUnknownTarget
    return (
        <Sidebar collapsible="icon" variant="inset">
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton size="lg" asChild>
                            <AppLink href="/dashboard" className="flex w-full items-center justify-start p-0 group-data-[collapsible=icon]:justify-center">
                                <AppLogo />
                            </AppLink>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            <SidebarContent>
                <NavMain items={visibleMainItems} />
            </SidebarContent>

            <SidebarFooter>
                <NavFooter items={visibleFooterItems} className="mt-auto" />
                <NavUser />
            </SidebarFooter>
        </Sidebar>
    );
}
